import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BaseComponent } from '@core/base.component';
import { generateXML } from '@core/util/eregistration';
import { createId, downloadFile, getFileFromBase64 } from '@core/util/file';
import { getMortgagePrimaryLender } from '@core/util/mortgage';
import { ERegChargeTag } from '@core/util/tags';
import {
	MatterChargeInstrument,
	MatterFileType,
	MatterTitle,
} from '@models/matter';
import { MatterDocumentType, MatterFile } from '@models/matter-file';
import { MatterMortgageWithAssociations } from '@models/mortgage';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Store } from '@ngxs/store';
import { ClipboardService } from '@services/clipboard/clipboard.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { FileUploadState } from '@state/file-upload/fileUpload.state';
import { EregXmlSelectors } from '@state/global/ereg-xml';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import {
	MatterMortgageDispatch,
	MatterMortgagesState,
} from '@state/matter/matter_mortgages';
import { MatterUploadedFilesSelectors } from '@state/matter/uploaded-files/matter-uploaded-files.selectors';
import { UploadFileStatus } from '@state/utils/models';
import { addDays, isAfter } from 'date-fns';
import { cloneDeep } from 'lodash';
import LogRocket from 'logrocket';
import { Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ButtonGroupOption } from 'src/app/shared/atomic/atoms/button-group/button-group.component';
import { BUTTON_PRESETS } from 'src/app/ui/button/button.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { EregFileViewerComponent } from '../components/ereg-file-viewer/ereg-file-viewer.component';
import {
	ReviewXmlFieldsComponent,
	ReviewXmlFieldsComponentData,
} from '../components/review-xml-fields/review-xml-fields.component';
import { AI_GENERATED_ICON, ICON } from '@core/util/icon';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import { NavigationService } from '@services/nav/nav.service';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { toSignal } from '@angular/core/rxjs-interop';

enum ChargePageTab {
	XML = 'xml',
	REGISTRATION = 'registration',
}

const tabs: ButtonGroupOption<ChargePageTab>[] = [
	{
		value: ChargePageTab.XML,
		label: 'Charge XML',
		intercomAttr: 'charge-xml-tab',
	},
	{
		value: ChargePageTab.REGISTRATION,
		label: 'Registration Details',
		intercomAttr: 'charge-registration-details-tab',
	},
];

const NUM_OF_EREG_FIELDS = 18;

interface VM {
	mortgages: MatterMortgageWithAssociations[];
	selectedMortgage: MatterMortgageWithAssociations;
	selectedMortgageId: string;
	logoUrl: string;
	selectedTab: ChargePageTab;
	systemTag: string;
	disableCopyButton: boolean;
	eregPublicXmlUrl?: string;
	chargeFile?: MatterFile;
	assignmentOfRentsFile?: MatterFile;
	generalSecurityAgreementFile?: MatterFile;
	numOfEregFields?: number;
	files?: MatterFile[];
	canImportChargeXml?: boolean;
	missingFields: {
		message: string;
		action: () => void;
	}[];
}

const defaultVM: VM = {
	mortgages: null,
	selectedMortgage: null,
	selectedMortgageId: '',
	logoUrl: '',
	selectedTab: ChargePageTab.XML,
	systemTag: '',
	eregPublicXmlUrl: '',
	disableCopyButton: false,
	chargeFile: null,
	assignmentOfRentsFile: null,
	numOfEregFields: 0,
	generalSecurityAgreementFile: null,
	files: [],
	canImportChargeXml: false,
	missingFields: [],
};

@UntilDestroy()
@Component({
	selector: 'app-charge',
	templateUrl: './charge.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ChargeComponent extends BaseComponent<VM> implements OnInit {
	public AnalyticsEvent = AnalyticsEvent;
	public AI_ICON = AI_GENERATED_ICON.icon;
	public AI_ICON_CLASSES = AI_GENERATED_ICON.classes;
	public AI_TOOLTIP = AI_GENERATED_ICON.tooltip;
	public BUTTON_PRESETS = BUTTON_PRESETS;
	public NUM_OF_EREG_FIELDS = NUM_OF_EREG_FIELDS;
	public MatterDocumentType = MatterDocumentType;

	public mortgages$ = this.store.select(
		MatterMortgagesState.getAllMortgagesWithAssociations,
	);
	public chargeXml$ = this.store.select(EregXmlSelectors.getChargeXml);
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.matterFileType) matterType: MatterFileType;
	@SelectSnapshot(SelectedMatter.closingDate) closingDate: string;
	@SelectSnapshot(MatterTitlesState.getAll) matterTitles: MatterTitle[];
	public getLatestChargeFile$ = this.store.select(
		MatterUploadedFilesSelectors.getLatestERegChargeAndSystemTag,
	);
	public getAssignmentOfRentsByTag$ = this.store.select(
		MatterUploadedFilesSelectors.getLatestAssignmentOfRentsByTag,
	);
	public getGeneralSecurityAgreementByTag$ = this.store.select(
		MatterUploadedFilesSelectors.getLatestGeneralSecurityAgreementByTag,
	);
	public fileStatusFilter$ = this.store.select(FileUploadState.getFileStatus);
	@SelectSnapshot(SelectedMatter.interestEstate) interestEstate: string;
	public runMatterDocumentAiScrapings = toSignal(
		this.store.select(SelectedMatter.runMatterDocumentAiScrapings),
	);

	public tabs = tabs;
	public ChargePageTab = ChargePageTab;
	public chargeXml;
	public chargeObject;
	public chargeObjectHardCopy: MatterChargeInstrument;
	public enableXmlDownload = false;
	public chargeXmlSubscription: Subscription;
	public chargeFileSubscription: Subscription;
	public assignmentOfRentsFileSubscription: Subscription;
	public generalSecurityAgreementSubscription: Subscription;
	public ICON = ICON;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private mortgageDispatch: MatterMortgageDispatch,
		private matterDispatch: MatterDispatch,
		private drawerService: DrawerService,
		private snackbarService: SnackbarService,
		private clipboard: ClipboardService,
		private navigationService: NavigationService,
		private store: Store,
		private analytics: AnalyticsService,
	) {
		super(defaultVM);
	}

	public ngOnInit(): void {
		this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe(() => {
				const mortgageId = this.route.snapshot.paramMap.get('mortgageId');

				this.selectMortgage(mortgageId);
			});

		this.mortgages$.pipe(untilDestroyed(this)).subscribe((mortgages) => {
			this.VM = {
				mortgages,
			};

			const mortgageId = this.route.snapshot.paramMap.get('mortgageId');
			if (mortgageId && !this.VM.selectedMortgageId) {
				this.selectMortgage(mortgageId);
			}

			if (this.VM.selectedMortgageId) {
				this.selectMortgage(this.VM.selectedMortgageId);
			}
		});
	}

	public selectMortgage(mortgageId: string): void {
		const mortgage = this.VM.mortgages.find((m) => m.id === mortgageId);

		const logoUrl = getMortgagePrimaryLender(mortgage)?.logoUrl;

		if (this.chargeXmlSubscription) {
			this.chargeXmlSubscription.unsubscribe();
			this.chargeObjectHardCopy = null;
		}
		if (this.chargeFileSubscription) {
			this.chargeFileSubscription.unsubscribe();
		}
		if (this.assignmentOfRentsFileSubscription) {
			this.assignmentOfRentsFileSubscription.unsubscribe();
		}
		if (this.generalSecurityAgreementSubscription) {
			this.generalSecurityAgreementSubscription.unsubscribe();
		}

		if (mortgageId !== this.VM.selectedMortgageId) {
			const closingDate = new Date(this.closingDate);
			const currentDate = new Date();
			const dayAfterClosingDate = addDays(closingDate, 1);
			this.VM = {
				selectedTab:
					this.closingDate && isAfter(currentDate, dayAfterClosingDate)
						? ChargePageTab.REGISTRATION
						: ChargePageTab.XML,
			};
		}

		const systemTag = ERegChargeTag(mortgage.id);
		this.VM = {
			selectedMortgageId: mortgageId,
			selectedMortgage: mortgage,
			logoUrl,
			systemTag,
		};

		this.chargeXmlSubscription = this.getChargeXmlSubscription();

		this.chargeFileSubscription = this.getChargeFile$(systemTag)
			.pipe(untilDestroyed(this))
			.subscribe((chargeFile) => {
				this.VM = { chargeFile };
			});

		this.assignmentOfRentsFileSubscription = this.getAssignmentOfRentsFile$(
			systemTag,
		)
			.pipe(untilDestroyed(this))
			.subscribe((assignmentOfRentsFile) => {
				this.VM = { assignmentOfRentsFile };
			});

		this.generalSecurityAgreementSubscription =
			this.getGeneralSecurityAgreementFile$(systemTag)
				.pipe(untilDestroyed(this))
				.subscribe((generalSecurityAgreementFile) => {
					this.VM = { generalSecurityAgreementFile };
				});
	}

	getChargeXmlSubscription(): Subscription {
		return this.getChargeXml$(this.VM.selectedMortgageId)
			.pipe(untilDestroyed(this))
			.subscribe((object) => {
				this.chargeXml = generateXML(object);
				if (!this.chargeObjectHardCopy) {
					this.chargeObjectHardCopy = cloneDeep(
						object,
					) as MatterChargeInstrument;
				}
				this.chargeObject = object;
				this.enableXmlDownload = true;

				this.VM = {
					canImportChargeXml:
						this.chargeObjectHardCopy?.Instrument?.Amount &&
						this.matterTitles?.length > 0,
					numOfEregFields: Object.keys(object?.Instrument)?.length,
					missingFields: this.getMissingFields(),
				};
			});
	}

	getChargeXml$(mortgageId: string) {
		return this.chargeXml$.pipe(
			untilDestroyed(this),
			map((filter) => filter(mortgageId)),
		);
	}

	download() {
		downloadFile(this.chargeObject.Instrument.Name, this.chargeXml, 'xml');
	}

	copyXMLToClipboard() {
		if (this.VM.eregPublicXmlUrl) {
			this.clipboard.copyText(this.VM.eregPublicXmlUrl, {
				success: {
					text: 'The XML file URL has been copied to your clipboard.',
					type: 'success',
				},
			});
		} else {
			this.createXMLUrl();
		}
	}

	setRegistrationNumber(event: string) {
		this.mortgageDispatch.updateMortgage(this.VM.selectedMortgageId, {
			registrationNumber: event,
		});
	}

	setRegistrationDate(event: string) {
		this.mortgageDispatch.updateMortgage(this.VM.selectedMortgageId, {
			registrationDate: event,
		});
	}

	toggleAssignmentOfRents() {
		this.mortgageDispatch.updateMortgage(this.VM.selectedMortgageId, {
			assignmentOfRents: !this.VM.selectedMortgage.assignmentOfRents,
		});
	}

	updateAssignmentOfRentsRegDate($event: string): void {
		this.mortgageDispatch.updateMortgage(this.VM.selectedMortgageId, {
			assignmentOfRentsRegDate: $event,
		});
	}

	updateAssignmentOfRentsRegNumber($event: string): void {
		this.mortgageDispatch.updateMortgage(this.VM.selectedMortgageId, {
			assignmentOfRentsRegNumber: $event,
		});
	}

	updateAssignmentOfRentsPpsaRegNumber($event: string): void {
		this.mortgageDispatch.updateMortgage(this.VM.selectedMortgageId, {
			assignmentOfRentsPpsaRegNumber: $event,
		});
	}

	toggleGeneralSecurityAgreement() {
		this.mortgageDispatch.updateMortgage(this.VM.selectedMortgageId, {
			generalSecurityAgreement:
				!this.VM.selectedMortgage.generalSecurityAgreement,
		});
	}

	updateGeneralSecurityAgreementRegDate($event: string) {
		this.mortgageDispatch.updateMortgage(this.VM.selectedMortgageId, {
			generalSecurityAgreementRegDate: $event,
		});
	}

	updateGeneralSecurityAgreementRegNumber($event: string) {
		this.mortgageDispatch.updateMortgage(this.VM.selectedMortgageId, {
			generalSecurityAgreementRegNumber: $event,
		});
	}

	viewFile(file: MatterFile) {
		this.drawerService.openDrawer(EregFileViewerComponent, {
			title: file.name,
			noStyle: true,
			data: {
				file,
			},
		});
	}

	openXMLDetails() {
		this.drawerService.openDrawer(ReviewXmlFieldsComponent, {
			title: 'Review Charge XML Details',
			data: {
				chargeObject: this.chargeObjectHardCopy,
				mortgage: this.VM.selectedMortgage,
				matterId: this.matterId,
				matterType: this.matterType,
				interestEstate: this.interestEstate,
				matterTitles: this.matterTitles,
				missingFields: this.VM.missingFields,
			} as ReviewXmlFieldsComponentData,
			size: 'sm',
		});
	}

	createXMLUrl() {
		if (!this.VM.disableCopyButton) {
			const file = getFileFromBase64(
				window.btoa(this.chargeXml),
				this.chargeObject.Instrument.Name,
				'text/xml',
			);
			const docId = createId();

			this.matterDispatch.UploadEregXMLFile(docId, this.matterId, file);

			this.getFileStatus(docId)
				.pipe(untilDestroyed(this))
				.subscribe((status) => {
					if (status.status === UploadFileStatus.SUCCESS) {
						this.snackbarService.openSnackBar({
							text: 'New XML file URL successfully created',
							type: 'success',
						});
						this.VM = {
							eregPublicXmlUrl: status.publicUrl,
						};
						LogRocket.info('Teraview public url', status.publicUrl);
						this.clipboard.copyText(this.VM.eregPublicXmlUrl, {
							success: {
								text: 'The XML file URL has been copied to your clipboard.',
								type: 'success',
							},
						});
					}
				});
		}
	}

	getFileStatus(id: string) {
		return this.fileStatusFilter$.pipe(
			untilDestroyed(this),
			map((func) => func(id)),
		);
	}

	getChargeFile$(tag: string): Observable<MatterFile> {
		return this.getLatestChargeFile$.pipe(
			untilDestroyed(this),
			map((fn) => fn(tag)),
		);
	}

	getAssignmentOfRentsFile$(tag: string): Observable<MatterFile> {
		return this.getAssignmentOfRentsByTag$.pipe(
			untilDestroyed(this),
			map((fn) => fn(tag)),
		);
	}

	getGeneralSecurityAgreementFile$(tag: string): Observable<MatterFile> {
		return this.getGeneralSecurityAgreementByTag$.pipe(
			untilDestroyed(this),
			map((fn) => fn(tag)),
		);
	}

	getMissingFields(): { message: string; field: string; action: () => void }[] {
		const missingFields = [];

		if (
			!this.matterTitles?.length ||
			this.matterTitles.some((t) => !t.pinNumber)
		) {
			missingFields.push({
				message: 'PIN required to create Teraview instrument.',
				action: () => {
					this.navigationService.goToMatter_Property(this.matterId, {
						'back-string': 'overview',
					});
				},
			});
		}

		if (!this.chargeObject?.Instrument?.Amount) {
			missingFields.push({
				message: 'Principal amount required to create Teraview instrument.',
				action: () => {
					this.navigationService.goToMatter_Mortgages_Mortgage(
						this.matterId,
						this.VM.selectedMortgage.id,
					);
				},
			});
		}

		return missingFields;
	}

	onFileUploadSuccess(): void {
		this.analytics.logEvent(
			this.runMatterDocumentAiScrapings()?.[MatterDocumentType.EREG_CHARGE]
				? AnalyticsEvent.EREG_CHARGE_AI_ON
				: AnalyticsEvent.EREG_CHARGE_AI_OFF,
		);
	}
}
